import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectRiskEntries,
  //selectAllControls
} from './redux/selectors/assessmentSelectors';
import { selectStageMonitor } from './redux/selectors/monitorSelectors';
import { runStageMonitor } from './redux/actions/monitorActions';
import { clearMonitor } from './redux/actions/monitorActions';
import StageReport from './StageReport';
import { Box, Paper, Button, Chip, Typography, IconButton, Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle, DialogContent } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconWithProgress from './IconWithProgress';
//import { useNavigate } from 'react-router-dom';



export default function StageMonitor({ modelId }) {
  const dispatch = useDispatch();
  //const navigate = useNavigate();
  const { loading, data } = useSelector(state => selectStageMonitor(state, modelId));

  const [showReport, setShowReport] = useState(false);

  // Selector data
  const entries = useSelector(state => selectRiskEntries(state, modelId));



  // Current stage from model slice
  const fromStage = useSelector(state => state.risks.byAssessment[modelId]?.modelStage);

  // Stages from config (with safe access)
  const stages = useSelector(state =>
    state.config?.configs?.modelFormConfig?.fields?.find(f => f.name === 'modelStage')?.options || []
  );

  // Local slider index state
  const [sliderIndex, setSliderIndex] = useState(
    stages ? Math.max(0, stages.indexOf(fromStage)) : 0
  );

  // Reset slider index on fromStage or stages change
  useEffect(() => {
    if (stages && stages.length > 0) {
      setSliderIndex(Math.max(0, stages.indexOf(fromStage)));
    }
  }, [fromStage, stages]);

  const selectedStage = stages && stages[sliderIndex];
  const changed = selectedStage !== fromStage;

  // Kick off the monitor thunk
  const handleRun = () => {
    dispatch(runStageMonitor({
      modelId,
      entries,
      fromStage,
      toStage: selectedStage
    }));
  };

  // If we have data, show summary and link to full report
  if (data) {
    const newRisksCount = Array.isArray(data.revision.newRisks)
      ? data.revision.newRisks.length
      : 0;
    const updateRisksCount = Array.isArray(data.revision.updateRisks)
      ? data.revision.updateRisks.length
      : 0;
    const controlsChangedCount =
      (Array.isArray(data.revision.updateCtrls)
        ? data.revision.updateCtrls.length
        : 0) +
      (Array.isArray(data.revision.newCtrls)
        ? data.revision.newCtrls.length
        : 0);

    return (
      <Box sx={{ p: 2 }}>
        <Paper
          variant="outlined"
          sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Typography variant="h6">
            Progress Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {data.fromStage} → {data.toStage}
          </Typography>
          <Table size="small" sx={{ my: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>New Risks</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Updated Risks</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Controls Changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  <Chip label={newRisksCount} color="success" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={updateRisksCount} color="warning" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={controlsChangedCount} color="info" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'center',  gap: 1 }}>
            <Button onClick={() => setShowReport(true)}>
              VIEW REPORT
            </Button>
            <Button
              startIcon={<ReplayIcon />}
              onClick={() => dispatch(clearMonitor(modelId))}
            >
              CLEAR
            </Button>
          </Box>
        </Paper>
        <Dialog
          open={showReport}
          onClose={() => setShowReport(false)}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Analysis Report: {data.fromStage} → {data.toStage}
          </DialogTitle>
          <DialogContent dividers>
            <StageReport
              revision={data.revision}
              entries={entries}
              from={data.fromStage}
              to={data.toStage}
              modelId={modelId}
              onClose={() => setShowReport(false)}
            />
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  // Otherwise show the trigger UI
  return (
    <Box sx={{ p: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Select Target Model Stage
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1">Current Stage:</Typography>
          <Chip label={fromStage || '—'} color="primary" />
        </Box>

        {/* 2) Target Stage (arrow navigation) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
          <Typography variant="subtitle1">Target Stage:</Typography>
          <IconButton
            onClick={() => setSliderIndex(Math.max(0, sliderIndex - 1))}
            disabled={sliderIndex === 0}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Chip label={selectedStage || '—'} color="primary" />
          <IconButton
            onClick={() => setSliderIndex(Math.min(stages.length - 1, sliderIndex + 1))}
            disabled={sliderIndex === stages.length - 1}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          disabled={!changed || loading}
          onClick={handleRun}
          startIcon={<IconWithProgress isLoading={loading} />}
        >
          Analyze Stage Change
        </Button>
      </Paper>
    </Box>
  );
}